import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { FolderKanban, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../hooks/useNotification';
import { PROJECTS_ROUTE, projectRoute } from '../constants';
import { useConversationDragActive } from '../hooks/useConversationDrag';
import { useMoveConversation } from '../hooks/useMoveConversation';
import { useProjects } from '../hooks/useProjects';
import { ProjectDropTarget } from './ProjectDropTarget';
import { ProjectFormDialog } from './ProjectFormDialog';

interface SidebarProjectsEntryProps {
  isCollapsed: boolean;
  isMobileDrawer: boolean;
  onNavigate?: () => void;
}

/** "Projects" card under "New chat": opens the project list; "+" creates a project. */
export const SidebarProjectsEntry: React.FC<SidebarProjectsEntryProps> = ({
  isCollapsed,
  isMobileDrawer,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { success } = useNotification();
  const { projects, enabled, isLoaded, canCreate, limit, createProject } = useProjects();
  const moveConversation = useMoveConversation();
  const isDragging = useConversationDragActive();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const showLabels = !isCollapsed || isMobileDrawer;
  const isActive = location.pathname.startsWith(PROJECTS_ROUTE);
  const canAdd = enabled && canCreate;
  const addTooltip = canAdd
    ? t('projects.sidebar.create')
    : isLoaded && !enabled
      ? t('projects.sidebar.notInPackage')
      : t('projects.sidebar.limitReached', { limit: limit ?? 0 });

  return (
    <div className={`px-3 pt-2 ${showLabels ? '' : 'flex justify-center'}`}>
      <div
        className={`flex items-center rounded-lg border transition-colors ${
          isActive
            ? 'border-blue-500/50 bg-blue-50 dark:border-blue-400/40 dark:bg-slate-700/70'
            : 'border-gray-200 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-700/50'
        } ${showLabels ? 'w-full' : ''}`}
      >
        <Tooltip title={showLabels ? '' : t('projects.sidebar.title')} placement="right" arrow>
          <Link
            to={PROJECTS_ROUTE}
            onClick={onNavigate}
            className={`flex min-h-10 min-w-0 flex-1 items-center gap-2 py-2 text-gray-700 dark:text-slate-200 ${
              showLabels ? 'pl-4' : 'justify-center px-2'
            }`}
          >
            <FolderKanban size={17} className="shrink-0" />
            {showLabels && (
              <Typography variant="body2" className="truncate font-semibold">
                {t('projects.sidebar.title')}
              </Typography>
            )}
          </Link>
        </Tooltip>
        {showLabels && (
          <Tooltip title={addTooltip} placement="right" arrow>
            <span>
              <IconButton
                size="small"
                aria-label={t('projects.sidebar.create')}
                disabled={!canAdd}
                onClick={() => setIsFormOpen(true)}
                className="mr-1 text-gray-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
              >
                <Plus size={16} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>

      {showLabels && isDragging && projects.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {projects.map((project) => (
            <li key={project.publicId}>
              <ProjectDropTarget
                onDropConversation={(sessionId) => void moveConversation(sessionId, project)}
                className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 dark:border-slate-600 dark:text-slate-300"
              >
                <FolderKanban size={14} className="shrink-0" />
                <span className="truncate">{project.name}</span>
              </ProjectDropTarget>
            </li>
          ))}
        </ul>
      )}

      <ProjectFormDialog
        open={isFormOpen}
        onSubmit={createProject}
        onClose={() => setIsFormOpen(false)}
        onSuccess={(project) => {
          success(t('projects.notifications.created', { name: project.name }));
          onNavigate?.();
          navigate(projectRoute(project.publicId));
        }}
      />
    </div>
  );
};
